import { Trophy, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { LeaderboardUser } from '@/types/leaderboard';

interface LeaderboardWidgetProps {
  title?: string;
  users: LeaderboardUser[];
}

// Default leaderboard data
const defaultLeaderboardUsers: LeaderboardUser[] = [
  { id: "d5eadcab-1ba1-4866-887a-8beeb134cdb8", name: 'Tamika Fillcot', username: '@tamikafillcot', avatar: '/placeholder.svg', level: 28, score: 1520, rank: 1, change: 2 },
  { id: "f7957368-81c7-428e-b992-8f47076b8b47", name: 'Patrick Newman', username: '@patricknewman', avatar: '/placeholder.svg', level: 24, score: 1340, rank: 2, change: -1 },
  { id: "4982e951-dec5-4962-a2d6-397974c152b0", name: 'Yulia Polischuk', username: '@meisquare', avatar: '/placeholder.svg', level: 19, score: 1120, rank: 3, change: 1 },
  { id: "d5eadcab-1ba1-4866-887a-8beeb134cdb8", name: 'Amanda Freeze', username: '@meow_amanda97', avatar: '/placeholder.svg', level: 16, score: 980, rank: 4, change: -2 },
  { id: "f7957368-81c7-428e-b992-8f47076b8b47", name: 'Anatoly Belik', username: '@belik_anatoly', avatar: '/placeholder.svg', level: 15, score: 860, rank: 5, change: 0 },
];

const LeaderboardWidget = ({ title = "Top Performers", users = defaultLeaderboardUsers }: LeaderboardWidgetProps) => {
  return (
    <div className="space-y-3 vibebops-card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="h-4 w-4 text-yellow-500" />
          <h3 className="text-sm font-extrabold text-vibebops-textMuted uppercase">
            {title}
          </h3>
        </div>
        <Link 
          to="/leaderboard" 
          className="text-xs font-bold text-vibebops-accent hover:underline border-b-2 border-vibebops-accent pb-0.5"
        >
          View All
        </Link>
      </div>
      
      <div className="space-y-2">
        {users.slice(0, 5).map((user) => (
          <Link 
            key={user.id} 
            to={`/profile/${user.id}`} 
            className="flex items-center gap-2 p-2 rounded-lg border-2 border-black bg-vibebops-highlight shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-all"
          >
            <div className="text-sm font-bold w-5 text-center">
              {user.rank === 1 && '🥇'}
              {user.rank === 2 && '🥈'}
              {user.rank === 3 && '🥉'}
              {user.rank > 3 && user.rank}
            </div>
            
            <Avatar className="h-8 w-8 flex-shrink-0 border-2 border-black">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold">
                {user.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{user.name}</p>
              <p className="text-xs text-vibebops-textMuted truncate">{user.username}</p>
            </div>
            
            <div className="flex flex-col items-end">
              <div className="text-xs font-bold">
                <span className="text-xs px-1 py-0.5 rounded-full bg-vibebops-accent border-2 border-black text-white shadow-[1px_1px_0px_0px_rgba(0,0,0,1)]">
                  {user.score}
                </span>
              </div>
              {user.change !== undefined && user.change !== 0 && (
                <div className={`text-xs flex items-center font-bold ${user.change > 0 ? 'text-green-500' : 'text-red-500'}`}>
                  <TrendingUp className="h-3 w-3 mr-0.5" />
                  {user.change > 0 ? '+' : ''}{user.change}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default LeaderboardWidget;
